/* eslint-disable no-unused-vars */
"use client"

import { useEffect, useState, useRef, Fragment } from "react"
import { useDispatch, useSelector } from "react-redux"
import {
  closeChatAction,
  deleteOrderMessageAction,
  getOrderMessageListAction,
  sendLeadMessageLeadAction,
  uploadImageAction,
} from "../../services/action/common"
import socket from "../socket/socket"
import EmojiPicker from "emoji-picker-react"
import {
  Send,
  Smile,
  Paperclip,
  X,
  MoreVertical,
  Eye,
  EyeOff,
  Reply,
  Edit2,
  Trash2,
  Download,
  FileText,
  Maximize,
  Maximize2,
  Minimize,
  Minimize2,
  CheckIcon,
  Check,
} from "lucide-react"
import DeliverModal from "./components/DeliverModal"
import ReopenModal from "./components/ReOpenModal"
import SolutionUploadModal from "./components/SolutionUploadModal"
import QueryModal from "./components/QueryModal"
import { formatMessageTime } from "../utils/functions/functions"
import { OrderDetailsModal } from "./components/OrderDetailModel"
import { Toaster } from "../Toaster"

const userTypeColors = {
  CLIENT: "#E91E63",
  EXPERT: "#4CAF50",
  OPERATOR: "#FF9800",
  MANAGER: "#9C27B0",
  ADMIN: "#2196F3",
  SUPER_ADMIN: "#673AB7",
}

// Add animation for warning message
const animateFadeIn = `
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
}
.animate-fadeIn {
  animation: fadeIn 0.3s ease-out forwards;
}
`

const getFileType = (url) => {
  const extension = url.split(".").pop()?.toLowerCase()
  const imageTypes = ["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp"]
  return imageTypes.includes(extension || "") ? "image" : "file"
}

const truncateFileName = (fileName, maxLength = 15) => {
  if (fileName.length <= maxLength) return fileName
  const extension = fileName.split(".").pop()
  const nameWithoutExtension = fileName.substring(0, fileName.lastIndexOf("."))
  const truncatedName = nameWithoutExtension.substring(0, maxLength - 3 - (extension?.length || 0))
  return `${truncatedName}...${extension}`
}

const downloadFile = async (url, filename) => {
  try {
    const response = await fetch(url)
    const blob = await response.blob()
    const downloadUrl = window.URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = downloadUrl
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(downloadUrl)
  } catch (error) {
    console.error("Error downloading file:", error)
  }
}

const downloadAllFiles = async (attachments) => {
  for (const attachment of attachments) {
    await downloadFile(attachment.url, attachment.name)
  }
}

export default function WhatsAppStyleInternalChat() {
  const { user } = useSelector((state) => state.authenticationReducer)
  const { leadChats } = useSelector((state) => state.commonReducer)
  const [isMinimized, setIsMinimized] = useState(false)
  const [isMaximized, setIsMaximized] = useState(false)
  const [showHeaderMenu, setShowHeaderMenu] = useState(false)
  const [showDeliverModal, setShowDeliverModal] = useState(false)
  const [showReopenModal, setShowReopenModal] = useState(false)
  const [showSolutionUpload, setShowSolutionUpload] = useState(false)
  const [showQueryModal, setShowQueryModal] = useState(false)
  const [replyingTo, setReplyingTo] = useState(null)
  const [messageContent, setMessageContent] = useState("")
  const [fileAttachments, setFileAttachments] = useState([])
  const [showMessageActions, setShowMessageActions] = useState(null)
  const [isEditing, setIsEditing] = useState(null)
  const [editedMessage, setEditedMessage] = useState("")
  const [messages, setMessages] = useState([])
  const [leadDetails, setLeadDetails] = useState(null)
  const [highlightedMessageId, setHighlightedMessageId] = useState(null)
  const [newMessageIds, setNewMessageIds] = useState([])
  const [showEmojiPicker, setShowEmojiPicker] = useState(false)

  const dispatch = useDispatch()
  const messagesEndRef = useRef(null)
  const audioRef = useRef(null)
  const messageRefs = useRef({})
  const messagesContainerRef = useRef(null)

  // Add the animation styles to the document head
  useEffect(() => {
    const styleElement = document.createElement("style")
    styleElement.innerHTML = animateFadeIn
    document.head.appendChild(styleElement)
    return () => {
      document.head.removeChild(styleElement)
    }
  }, [])

  useEffect(() => {
    socket.connect()

    socket.on("new_lead_message_coming", (data) => {
      if (data.leadId === leadDetails?._id) {
        getChatList({ leadId: data.leadId })
        setNewMessageIds((prev) => [...prev, data.messageId])
      }
      if (audioRef.current) {
        audioRef.current.play()
      }
    })

    return () => {
      socket.off("new_lead_message_coming")
      socket.disconnect()
    }
  }, [leadDetails])

  useEffect(() => {
    if (leadChats?.messages) {
      setMessages(leadChats?.messages)
    }
    if (leadChats?.leadDetails) {
      setLeadDetails(leadChats.leadDetails)
    }
  }, [leadChats])

  useEffect(() => {
    scrollToBottom()
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
  }, [leadChats, messages])

  useEffect(() => {
    // Scroll to bottom when new messages are added
    if (messages.length > 0) {
      scrollToBottom()
    }
  }, [messages.length])

  useEffect(() => {
    if (newMessageIds.length > 0) {
      const timer = setTimeout(() => {
        setNewMessageIds([])
      }, 5000)
      return () => clearTimeout(timer)
    }
  }, [newMessageIds])

  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      setTimeout(() => {
        messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight
      }, 100) // Small delay to ensure content is rendered
    }
  }

  const getChatList = ({ leadId }) => {
    dispatch(getOrderMessageListAction({ data: { leadId } }))
  }

  const handleSendMessage = (visibleTo = ["CLIENT", "EXPERT", "OPERATOR"]) => {
    if (messageContent.trim() === "" && fileAttachments.length === 0) return
    if (!messageContent) {
      Toaster.error("Message is required")
      return false
    }

    const messageData = {
      leadId: leadDetails?._id,
      content: messageContent,
      messageType: fileAttachments.length > 0 ? "mixed" : "text",
      sender: user?._id,
      senderId: user?._id,
      visibleTo: ["ADMIN", "SUPER_ADMIN", "MANAGER", ...visibleTo],
      replyTo: replyingTo?.id || null,
      attachments: fileAttachments,
      timestamp: new Date().toISOString(),
    }

    dispatch(sendLeadMessageLeadAction({ data: messageData }))
    setMessageContent("")
    setReplyingTo(null)
    setFileAttachments([])
  }

  const uploadImage = (e) => {
    const callBackImage = (res) => {
      if (res?.imageUrls) {
        const newAttachments = res.imageUrls.map((url) => ({
          url,
          name: url.split("/").pop() || "Untitled",
        }))
        setFileAttachments((prev) => [...prev, ...newAttachments])
      }
    }
    const formData = new FormData()
    if (e.target.files) {
      for (let i = 0; i < e.target.files.length; i++) {
        formData.append("images", e.target.files[i])
      }
      dispatch(uploadImageAction({ data: formData, cb: callBackImage }))
      e.target.value = ""
    }
  }

  const cancelReply = () => {
    setReplyingTo(null)
  }

  const scrollToMessage = (messageId) => {
    setHighlightedMessageId(messageId)
    messageRefs.current[messageId]?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    })
    setTimeout(() => {
      setHighlightedMessageId(null)
      setNewMessageIds((prev) => prev.filter((id) => id !== messageId))
    }, 2000)
  }

  const toggleMessageVisibility = (message, role) => {
    let tempUserVisiblity = []
    if (message?.visibleTo?.some((itm) => itm?.toUpperCase() == role?.toUpperCase())) {
      tempUserVisiblity = message?.visibleTo?.filter((itm) => itm?.toUpperCase() != role?.toUpperCase())
    } else {
      tempUserVisiblity = [...message?.visibleTo, role?.toUpperCase()]
    }
    dispatch(
      deleteOrderMessageAction({ userType, data: { messageId: message?.id, data: { visibleTo: tempUserVisiblity } } }),
    )
  }

  const changeMasking = ({ message, masking }) => {
    dispatch(deleteOrderMessageAction({ userType, data: { messageId: message?.id, data: { isMasking: masking } } }))
  }

  const handleEditMessage = (messageId) => {
    const messageToEdit = messages.find((m) => m.id === messageId)
    if (messageToEdit) {
      setIsEditing(messageId)
      setEditedMessage(messageToEdit.content || messageToEdit.text || "")
      setShowMessageActions(null)
    }
  }

  const userType = user?.userType

  const handleSaveEdit = (messageId) => {
    dispatch(deleteOrderMessageAction({ userType, data: { messageId, data: { oldMessage: editedMessage } } }))
    setIsEditing(null)
    setEditedMessage("")
  }

  const handleDeleteMessage = (messageId, visibleTo) => {
    dispatch(
      deleteOrderMessageAction({ userType, data: { messageId, data: { visibleTo: [...visibleTo, "DELETED"] } } }),
    )
    setShowMessageActions(null)
  }

  const handleEmojiSelect = (emojiObject) => {
    setMessageContent((prevContent) => prevContent + emojiObject.emoji)
    setShowEmojiPicker(false)
  }

  return (
    leadChats && (
      <div
        className={`fixed ${
          isMaximized ? "inset-0" : "bottom-4 right-4"
        } flex flex-col bg-gradient-to-r from-[#00234a] to-[#001a38] rounded-lg shadow-2xl transition-all duration-300 ease-in-out
      ${isMinimized ? "h-14 w-80" : isMaximized ? "h-[100%] w-[100%]" : "h-[600px] w-[400px]"}`}
        style={{ zIndex: 51 }}
      >
        <audio ref={audioRef} src="/assets/seen.wav" />

        <ChatHeader
          leadDetails={leadDetails}
          isMinimized={isMinimized}
          setIsMinimized={setIsMinimized}
          isMaximized={isMaximized}
          setIsMaximized={setIsMaximized}
          showHeaderMenu={showHeaderMenu}
          setShowHeaderMenu={setShowHeaderMenu}
          setShowSolutionUpload={setShowSolutionUpload}
          setShowDeliverModal={setShowDeliverModal}
          setShowReopenModal={setShowReopenModal}
          setShowQueryModal={setShowQueryModal}
        />

        {isMinimized ? (
          <div className="flex-1 flex items-center justify-between px-4">
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <span className="text-white text-sm font-medium">Active Chat</span>
            </div>
            <span className="text-white text-xs">{messages.length} messages</span>
          </div>
        ) : (
          <>
            <ChatMessages
              messages={messages}
              user={user}
              messageRefs={messageRefs}
              messagesEndRef={messagesEndRef}
              highlightedMessageId={highlightedMessageId}
              newMessageIds={newMessageIds}
              isEditing={isEditing}
              editedMessage={editedMessage}
              setEditedMessage={setEditedMessage}
              handleSaveEdit={handleSaveEdit}
              setIsEditing={setIsEditing}
              setReplyingTo={setReplyingTo}
              showMessageActions={showMessageActions}
              setShowMessageActions={setShowMessageActions}
              handleEditMessage={handleEditMessage}
              handleDeleteMessage={handleDeleteMessage}
              toggleMessageVisibility={toggleMessageVisibility}
              scrollToMessage={scrollToMessage}
              userTypeColors={userTypeColors}
              getFileType={getFileType}
              truncateFileName={truncateFileName}
              downloadFile={downloadFile}
              downloadAllFiles={downloadAllFiles}
              messagesContainerRef={messagesContainerRef}
              leadDetails={leadDetails}
              changeMasking={changeMasking}
            />

            {/* Scroll to bottom button */}
            <button
              onClick={scrollToBottom}
              className="fixed bottom-24 right-8 p-2.5 bg-[#002146] text-white rounded-full shadow-lg hover:bg-[#001a38] transition-all z-10"
              style={{ opacity: isMaximized ? 0.8 : 0.6 }}
              title="Scroll to bottom"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </button>

            <ChatInput
              replyingTo={replyingTo}
              cancelReply={cancelReply}
              messages={messages}
              showEmojiPicker={showEmojiPicker}
              setShowEmojiPicker={setShowEmojiPicker}
              handleEmojiSelect={handleEmojiSelect}
              uploadImage={uploadImage}
              messageContent={messageContent}
              setMessageContent={setMessageContent}
              handleSendMessage={handleSendMessage}
              fileAttachments={fileAttachments}
              truncateFileName={truncateFileName}
            />
          </>
        )}

        {showDeliverModal && user?.userType !== "CLIENT" && (
          <DeliverModal setShowDeliverModal={setShowDeliverModal} leadDetails={leadDetails} />
        )}
        {showReopenModal && <ReopenModal setShowReopenModal={setShowReopenModal} leadDetails={leadDetails} />}
        {showSolutionUpload && (
          <SolutionUploadModal setShowSolutionUpload={setShowSolutionUpload} leadDetails={leadDetails} />
        )}
        {showQueryModal && <QueryModal setShowQueryModal={setShowQueryModal} leadDetails={leadDetails} />}
      </div>
    )
  )
}

function ChatHeader({
  leadDetails,
  isMinimized,
  setIsMinimized,
  isMaximized,
  setIsMaximized,
  showHeaderMenu,
  setShowHeaderMenu,
  setShowSolutionUpload,
  setShowDeliverModal,
  setShowReopenModal,
  setShowQueryModal,
}) {
  const { user } = useSelector((state) => state.authenticationReducer)
  const [showDetailsModal, setShowDetailsModal] = useState(false)
  const isIsAdmin =
    user?.userType === "ADMIN" ||
    user?.userType === "SUPER_ADMIN" ||
    user?.userType === "MANAGER" ||
    user?.userType === "OPERATOR"

  const isExpert = user?.userType === "EXPERT"
  const dispatch = useDispatch()
  return (
    <div className="flex items-center justify-between text-white p-2.5 rounded-t-lg bg-gradient-to-r from-[#001a38] to-[#00234a]">
      <div className="flex items-center space-x-3">
        <div className="relative">
          <button
            onClick={() => setShowHeaderMenu(!showHeaderMenu)}
            className="p-1.5 hover:bg-white/20 rounded-full transition-colors"
          >
            <MoreVertical className="w-4 h-4" />
          </button>
          {showHeaderMenu && (
            <div className="absolute top-full left-0 mt-1 bg-white rounded-lg shadow-xl border p-2 z-50">
              {isIsAdmin && (
                <Fragment>
                  <button
                    onClick={() => {
                      setShowDeliverModal(true)
                      setShowHeaderMenu(false)
                    }}
                    className="w-full min-w-[156px] text-left px-3 py-2 text-sm hover:bg-gray-100 rounded text-gray-700"
                  >
                    Deliver Order
                  </button>
                  <button
                    onClick={() => {
                      setShowReopenModal(true)
                      setShowHeaderMenu(false)
                    }}
                    className="w-full min-w-[156px] text-left px-3 py-2 text-sm hover:bg-gray-100 rounded text-gray-700"
                  >
                    Reopen Order
                  </button>
                </Fragment>
              )}

              {isExpert && (
                <Fragment>
                  <button
                    onClick={() => {
                      setShowSolutionUpload(true)
                      setShowHeaderMenu(false)
                    }}
                    className="w-full min-w-[156px] text-left px-3 py-2 text-sm hover:bg-gray-100 rounded text-gray-700"
                  >
                    Upload Solution
                  </button>
                  <button
                    onClick={() => {
                      setShowQueryModal(true)
                      setShowHeaderMenu(false)
                    }}
                    className="w-full min-w-[156px] text-left px-3 py-2 text-sm hover:bg-gray-100 rounded text-gray-700"
                  >
                    Submit Query
                  </button>
                </Fragment>
              )}
              <button
                onClick={() => {
                  setShowDetailsModal(true)
                  setShowHeaderMenu(false)
                }}
                className="w-full min-w-[156px] text-left px-3 py-2 text-sm hover:bg-gray-100 rounded text-gray-700"
              >
                View Details
              </button>
            </div>
          )}
        </div>
        <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center text-lg font-semibold">
          {leadDetails?.orderId?.[0] || "A"}
        </div>
        <div className="flex flex-col">
          <span className="text-sm font-semibold leading-tight">{leadDetails?.orderId || "A02P2639"}</span>
          <span className="text-[11px] text-emerald-400 leading-tight">
            {leadDetails?.orderStatus}{" "}
            {leadDetails?.orderStatus === "DELIVERED" &&
              leadDetails?.totalPayment > leadDetails?.paymentRecieved &&
              " (P)"}
          </span>
        </div>
      </div>
      <div className="flex items-center gap-1">
        <button
          onClick={() => {
            setIsMinimized(!isMinimized)
            setIsMaximized(false)
          }}
          className="p-1.5 hover:bg-white/20 rounded transition-colors"
          title={isMinimized ? "Expand" : "Minimize"}
        >
          {isMinimized ? (
            <Maximize2 className="w-4 h-4 text-white/70" />
          ) : (
            <Minimize2 className="w-4 h-4 text-white/70" />
          )}
        </button>
        <button
          onClick={() => {
            setIsMaximized(!isMaximized)
            setIsMinimized(false)
          }}
          className="p-1.5 hover:bg-white/20 rounded transition-colors"
          title={isMaximized ? "Restore" : "Maximize"}
        >
          {isMaximized ? (
            <Minimize className="w-4 h-4 text-white/70" />
          ) : (
            <Maximize className="w-4 h-4 text-white/70" />
          )}
        </button>
        <button
          onClick={() => {
            dispatch(closeChatAction())
          }}
          className="p-1.5 hover:bg-white/20 rounded transition-colors"
          title="Close"
        >
          <X className="w-4 h-4 text-white/70" />
        </button>
      </div>
      {showDetailsModal && <OrderDetailsModal leadDetails={leadDetails} onClose={() => setShowDetailsModal(false)} />}
    </div>
  )
}

function ChatMessages({
  messages,
  user,
  messageRefs,
  messagesEndRef,
  highlightedMessageId,
  newMessageIds,
  isEditing,
  editedMessage,
  setEditedMessage,
  handleSaveEdit,
  setIsEditing,
  setReplyingTo,
  showMessageActions,
  setShowMessageActions,
  handleEditMessage,
  handleDeleteMessage,
  toggleMessageVisibility,
  scrollToMessage,
  userTypeColors,
  getFileType,
  truncateFileName,
  downloadFile,
  downloadAllFiles,
  messagesContainerRef,
  leadDetails,
  changeMasking,
}) {
  const isClient = user?.userType === "CLIENT"
  const internalUsers = ["ADMIN", "SUPER_ADMIN", "OPERATOR", "MANAGER"]
  const isIsAdmin =
    user?.userType === "ADMIN" ||
    user?.userType === "SUPER_ADMIN" ||
    user?.userType === "MANAGER" ||
    user?.userType === "OPERATOR"

  const itsMe = (msgType) => {
    if (isIsAdmin && internalUsers?.includes(msgType?.senderType)) {
      return true
    } else if (user?._id === msgType?.senderId) {
      return true
    }
    return false
  }

  const renderMessageContent = (message) => {
    const isUserMessage = itsMe(message) || message?.messageType == "query"
    const textColor = isUserMessage ? "text-white" : "text-gray-700"
    const metaColor = isUserMessage ? "text-white/70" : "text-gray-400"

    const isExpert = user?.userType === "EXPERT"
    const formatedMessage = (msg, rootMessage) => {
      if (!rootMessage?.paymentLink) {
        return (messages = `Expert ${leadDetails?.expert?.name} has submitted the solution for order ${leadDetails?.orderId}.`)
      }

      return `<p class="text-sm font-sans">Dear ${leadDetails?.client?.name},</p>
        <p class="text-sm font-sans">Your order (ID: ${
          leadDetails?.orderId
        }) is ready for delivery. To complete the process and access your solution, please settle the remaining balance of ${
          leadDetails.totalPayment - leadDetails.paymentRecieved
        } ${leadDetails?.currancy}.</p>
        <p class="text-sm font-sans">You can make the payment through this secure link: <a href="${
          rootMessage?.paymentLink
        }" target="_blank" class="text-[15px] text-[#00e329] font-sans"> Click here to pay - ${
          leadDetails.totalPayment - leadDetails.paymentRecieved
        } ${leadDetails?.currancy}</a></p>
        <p class="text-sm font-sans">Once the payment is confirmed, you'll receive immediate access to your completed order.</p>
        <p class="text-sm font-sans">If you have any questions, please don't hesitate to contact our support team.</p>
        <p class="text-sm font-sans">Thank you for your business!</p>
      `
    }

    const statusChecks = (data) => {
      return [
        { label: "AI verified", value: "0%", status: data?.aiVerified },
        {
          label: "Plagiarism check",
          value: "Below 15%",
          status: data?.plagiarismCheck,
        },
        { label: "Grammar check", status: data?.grammarCheck },
        {
          label: "Word count requirements",
          status: data?.wordCountRequirement,
        },
        { label: "Rubric followed", status: data?.rubricFollowed },
      ]
    }

    return (
      <div className="space-y-3">
        {(message.content || message.text) &&
          (message.messageType === "expert_deliver" ? (
            <Fragment>
              <div
                className={`text-sm ${textColor} font-sans`}
                dangerouslySetInnerHTML={{
                  __html: formatedMessage(message.content || message.text, message),
                }}
              />

              {(isIsAdmin || (isExpert && message?.statusChecks)) &&
                !message?.visibleTo?.includes("CLIENT") &&
                statusChecks(message?.statusChecks).map((check, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <div
                      className={`flex items-center justify-center w-5 h-5 rounded-full ${
                        check.status ? "bg-green-100" : "bg-gray-100"
                      }`}
                    >
                      <CheckIcon className={`w-3 h-3 ${check.status ? "text-green-600" : "text-gray-400"}`} />
                    </div>
                    <span
                      className={`text-sm ${message.senderId === user?._id ? "text-white" : "text-gray-700"} font-sans`}
                    >
                      {check.label}
                    </span>
                    {check.value && <span className="text-sm text-gray-500 font-sans">({check.value})</span>}
                  </div>
                ))}
            </Fragment>
          ) : (
            <p className={`text-sm ${textColor} font-sans`}>{message.content || message.text}</p>
          ))}
        {message.attachments && message.attachments.length > 0 && (
          <div className="space-y-2">
            {message.attachments.length > 1 && (
              <button
                onClick={() => downloadAllFiles(message.attachments || [])}
                className={`flex items-center gap-1 text-xs ${
                  isUserMessage ? "text-white/90 hover:text-white" : "text-blue-500 hover:text-blue-600"
                } font-sans`}
              >
                <Download className="w-3 h-3" />
                Download All
              </button>
            )}
            <div className="grid gap-2">
              {message.attachments.map((attachment, index) => {
                const fileType = getFileType(attachment.url)
                const truncatedName = truncateFileName(attachment.name)
                return (
                  <div key={index} className="group relative">
                    {fileType === "image" ? (
                      <div className="relative group">
                        <img
                          src={attachment.url || "/placeholder.svg"}
                          alt={attachment.name}
                          className="max-w-full h-auto rounded-lg cursor-pointer"
                          style={{ maxWidth: "200px", maxHeight: "200px" }}
                          onClick={() => window.open(attachment.url, "_blank")}
                          onError={(e) => {
                            e.currentTarget.src = "/placeholder.svg"
                          }}
                        />
                        <div className="absolute inset-0 bg-black/20 flex items-center justify-center opacity-6 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              downloadFile(attachment.url, attachment.name)
                            }}
                            className={`p-2 rounded-full ${
                              isUserMessage ? "bg-white/30 hover:bg-white/50" : "bg-black/30 hover:bg-black/50"
                            } backdrop-blur-sm`}
                          >
                            <Download className={`w-5 h-5 ${isUserMessage ? "text-white" : "text-white"}`} />
                          </button>
                        </div>
                        <span
                          className={`absolute bottom-2 left-2 text-xs ${
                            isUserMessage ? "text-white/80" : "text-gray-600"
                          } bg-black/20 px-2 py-1 rounded font-sans`}
                        >
                          {truncatedName}
                        </span>
                      </div>
                    ) : (
                      <div
                        className={`flex items-center gap-2 p-2 rounded-lg ${
                          isUserMessage ? "bg-white/10" : "bg-gray-100"
                        }`}
                      >
                        <FileText className={`w-5 h-5 ${isUserMessage ? "text-white/70" : "text-gray-500"}`} />
                        <span className={`text-sm flex-1 truncate ${textColor} font-sans`}>{truncatedName}</span>
                        <button
                          onClick={() => downloadFile(attachment.url, attachment.name)}
                          className={`p-1.5 rounded-full ${isUserMessage ? "hover:bg-white/10" : "hover:bg-gray-200"}`}
                        >
                          <Download className={`w-4 h-4 ${isUserMessage ? "text-white/70" : "text-gray-500"}`} />
                        </button>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        )}
        {isIsAdmin && (
          <div className={`mt-2 text-[10px] ${metaColor} flex items-center font-sans`}>
            <Eye className="w-2.5 h-2.5 mr-1" />
            {message.visibleTo.join(", ")}
          </div>
        )}
      </div>
    )
  }

  return (
    <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-4 space-y-4 bg-white">
      {messages.map((message) => (
        <div
          key={message.id}
          className={`mb-4 flex ${itsMe(message) ? "justify-end" : "justify-start"}`}
          ref={(el) => {
            if (el) messageRefs.current[message.id] = el
          }}
        >
          <div
            className={`max-w-[70%] rounded-lg p-3 ${
              message?.messageType == "query" ? "bg-[#6e4646]" : itsMe(message) ? "bg-[#002146d4]" : "bg-gray-50"
            } shadow-md transition-all duration-300 ${
              highlightedMessageId === message.id
                ? "ring-2 ring-blue-400 bg-blue-50"
                : newMessageIds.includes(message.id)
                  ? "animate-pulse bg-green-100"
                  : ""
            }`}
          >
            <div className="flex items-center justify-between mb-1">
              <span
                className={`text-xs font-semibold ${itsMe(message) ? "text-white" : "text-gray-700"} font-sans`}
                style={{ color: userTypeColors[message.senderType] || "#000" }}
              >
                {isClient && message?.senderType === "EXPERT" ? message?.userId : message.sender} (
                {message?.senderType?.toLowerCase()}){" "}
              </span>
              <span
                className={`text-xs ${
                  message?.messageType == "query" || itsMe(message) ? "text-white" : "text-gray-500"
                } font-sans`}
              >
                {formatMessageTime(message.timestamp)}
              </span>
            </div>
            {message.replyTo && (
              <div
                className="mb-2 p-2 bg-gray-100 rounded text-xs text-gray-600 flex items-center cursor-pointer font-sans"
                onClick={() => scrollToMessage(message.replyTo?.id || "")}
              >
                <span className="truncate">Replying to: {message.replyTo.text}</span>
              </div>
            )}
            {isEditing === message.id ? (
              <div className="space-y-2">
                <textarea
                  value={editedMessage}
                  onChange={(e) => setEditedMessage(e.target.value)}
                  className="w-full border rounded-lg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002B5B] font-sans"
                />
                <div className="flex justify-end space-x-2">
                  <button
                    onClick={() => setIsEditing(null)}
                    className="px-2 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded font-sans"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleSaveEdit(message.id)}
                    className="px-2 py-1 text-xs bg-[#002B5B] text-white rounded hover:bg-[#001A38] font-sans"
                  >
                    Save
                  </button>
                </div>
              </div>
            ) : (
              <>{renderMessageContent(message)}</>
            )}
            <div className="mt-1 flex justify-end space-x-2">
              <button
                onClick={() => setReplyingTo(message)}
                className={`text-xs ${
                  itsMe(message) ? "text-white/80 hover:text-white" : "text-[#002B5B] hover:text-[#1E4D8B]"
                } flex items-center font-sans`}
              >
                <Reply className="w-4 h-4 mr-1" />
                Reply
              </button>
              {isIsAdmin && (
                <button
                  onClick={() => setShowMessageActions(showMessageActions === message.id ? null : message.id)}
                  className={`text-xs ${
                    itsMe(message) ? "text-white/80 hover:text-white" : "text-[#002B5B] hover:text-[#1E4D8B]"
                  } font-sans`}
                >
                  <MoreVertical className="w-4 h-4" />
                </button>
              )}
            </div>
            {showMessageActions === message.id && (
              <div className={`mt-2 space-y-1 ${itsMe(message) ? "bg-[#001A38]" : "bg-white"} rounded shadow-md p-2`}>
                {!message?.statusChecks && (
                  <button
                    onClick={() => handleEditMessage(message.id)}
                    className={`w-full text-left px-2 py-1 text-xs ${
                      itsMe(message) ? "hover:bg-[#002B5B] text-white" : "hover:bg-gray-100 text-gray-700"
                    } rounded flex items-center font-sans`}
                  >
                    <Edit2 className="w-3 h-3 mr-2" />
                    Edit
                  </button>
                )}
                {isIsAdmin && (
                  <button
                    onClick={() => handleDeleteMessage(message.id, message?.visibleTo)}
                    className={`w-full text-left px-2 py-1 text-xs ${
                      message.senderId === user?._id
                        ? "hover:bg-[#002B5B] text-red-300"
                        : "hover:bg-gray-100 text-red-500"
                    } rounded flex items-center font-sans`}
                  >
                    <Trash2 className="w-3 h-3 mr-2" />
                    Delete
                  </button>
                )}
                {isIsAdmin && <div className="border-t border-gray-200 my-1"></div>}
                {isIsAdmin &&
                  ["CLIENT", "EXPERT", "OPERATOR"].map((role) => (
                    <button
                      key={role}
                      onClick={() => toggleMessageVisibility(message, role)}
                      className={`w-full text-left px-2 py-1 text-xs ${
                        message.senderId === user?._id
                          ? "hover:bg-[#002B5B] text-white"
                          : "hover:bg-gray-100 text-gray-700"
                      } rounded flex items-center justify-between font-sans`}
                    >
                      <span>{role}</span>
                      {message.visibleTo.includes(role) ? (
                        <Eye className="w-3 h-3 text-green-500" />
                      ) : (
                        <EyeOff className="w-3 h-3 text-red-500" />
                      )}
                    </button>
                  ))}

                <button
                  key={"index_11111"}
                  onClick={() => changeMasking({ message, masking: !message?.isMasking })}
                  className={`w-full text-left px-2 py-1 text-xs ${
                    message.senderId === user?._id ? "hover:bg-[#002B5B] text-white" : "hover:bg-gray-100 text-gray-700"
                  } rounded flex items-center justify-between font-sans`}
                >
                  <span>Mask</span>
                  {!message?.isMasking ? (
                    <Eye className="w-3 h-3 text-green-500" />
                  ) : (
                    <EyeOff className="w-3 h-3 text-red-500" />
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      ))}
      <div ref={messagesEndRef} className="h-1" />
    </div>
  )
}

function ChatInput({
  replyingTo,
  cancelReply,
  messages,
  showEmojiPicker,
  setShowEmojiPicker,
  handleEmojiSelect,
  uploadImage,
  messageContent,
  setMessageContent,
  handleSendMessage,
  fileAttachments,
  truncateFileName,
}) {
  const { user } = useSelector((state) => state.authenticationReducer)
  const [showRecipientMenu, setShowRecipientMenu] = useState(false)
  const [selectedRecipients, setSelectedRecipients] = useState(["CLIENT", "EXPERT", "OPERATOR"])
  const [isInputFocused, setIsInputFocused] = useState(false)

  const toggleRecipient = (recipient) => {
    setSelectedRecipients((prev) =>
      prev.includes(recipient) ? prev.filter((r) => r !== recipient) : [...prev, recipient],
    )
  }

  const handleSendMessageWithRecipients = () => {
    handleSendMessage(selectedRecipients)
  }
  const isIsAdmin =
    user?.userType === "ADMIN" ||
    user?.userType === "SUPER_ADMIN" ||
    user?.userType === "MANAGER" ||
    user?.userType === "OPERATOR"

  return (
    <div className="bg-white p-3 border-t relative">
      {replyingTo && (
        <div className="mb-2 p-2 bg-gray-100 rounded-lg flex items-center justify-between">
          <div className="flex items-center">
            <span className="text-xs text-gray-500 truncate">
              Replying to: {messages.find((m) => m.id === replyingTo?.id)?.text}
            </span>
          </div>
          <button onClick={cancelReply} className="text-gray-400 hover:text-gray-600">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      <div className="flex items-center space-x-2">
        <div className="relative">
          <button
            onClick={() => setShowEmojiPicker(!showEmojiPicker)}
            className="p-2 text-gray-400 hover:text-gray-600"
          >
            <Smile className="w-6 h-6" />
          </button>
          {showEmojiPicker && (
            <div className="absolute bottom-12 left-0 z-10">
              <EmojiPicker onEmojiClick={handleEmojiSelect} emojiStyle="native" emojiSize={28} />
            </div>
          )}
        </div>
        {user?.userType != "EXPERT" && (
          <label className="p-2 text-gray-400 hover:text-gray-600 cursor-pointer">
            <input type="file" className="hidden" onChange={uploadImage} multiple />
            <Paperclip className="w-6 h-6" />
          </label>
        )}
        <input
          type="text"
          placeholder="Type a message"
          value={messageContent}
          onChange={(e) => setMessageContent(e.target.value)}
          onKeyPress={(e) => e.key === "Enter" && handleSendMessageWithRecipients()}
          onFocus={() => setIsInputFocused(true)}
          onBlur={() => setIsInputFocused(false)}
          className="flex-1 py-2 px-4 bg-gray-100 rounded-full focus:outline-none focus:ring-2 focus:ring-[#00234a]"
        />
        {isInputFocused && (
          <div
            className="absolute bg-yellow-300 text-red-600 font-medium text-center animate-fadeIn z-10"
            style={{
              left: "-10px",
              fontSize: "11px",
              padding: "5px !important",
              bottom: "68px",
              width: "100%",
            }}
          >
            Dear All, You are requested not to share any contact details here in the chat. Please connect with operators
            to make a call or schedule a meeting.
          </div>
        )}
        {isIsAdmin && (
          <div className="relative">
            <button
              onClick={() => setShowRecipientMenu(!showRecipientMenu)}
              className="p-2 text-gray-400 hover:text-gray-600"
            >
              <MoreVertical className="w-6 h-6" />
            </button>
            {showRecipientMenu && (
              <div className="absolute bottom-12 right-0 bg-white border rounded-lg shadow-lg z-10">
                {["CLIENT", "OPERATORS", "EXPERT", "ALL"].map((recipient) => (
                  <button
                    key={recipient}
                    onClick={() => toggleRecipient(recipient)}
                    className="block w-full text-left px-4 py-2 hover:bg-gray-100 text-xs"
                  >
                    <span className={selectedRecipients.includes(recipient) ? "text-[#00234a] font-semibold" : ""}>
                      {recipient}
                    </span>
                    {selectedRecipients.includes(recipient) && (
                      <Check className="inline-block w-3 h-3 ml-2 text-[#00234a]" />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        <button
          onClick={handleSendMessageWithRecipients}
          className="p-2 bg-[#00234a] text-white rounded-full hover:bg-[#001a38] transition-colors right-[34px] absolute"
        >
          <Send className="w-5 h-5" />
        </button>
      </div>
      {fileAttachments.length > 0 && (
        <div className="mt-2 p-2 bg-gray-100 rounded-lg">
          <p className="text-sm text-gray-600">Files attached: {fileAttachments.length}</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {fileAttachments.map((file, index) => (
              <div key={index} className="bg-white p-2 rounded shadow-sm text-xs">
                {truncateFileName(file.name, 15)}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

